import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environment';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import type { EmailDto } from '@sharedlib/dtos/dto/contacts-app/email.dto';
import type { PhoneDto } from '@sharedlib/dtos/dto/contacts-app/phone.dto';
import { type Observable } from 'rxjs';
import { type Contact } from '../types/contact';

export interface ContactVersion {
  readonly version: number;
  readonly kind: 'created' | 'baseline' | 'edited' | 'restored';
  readonly author: { readonly id: string; readonly name: string | null } | null;
  readonly at: string;
  /** The fields this save changed, by their stored names (title, jobTitle, company, emails, …). */
  readonly fields: readonly string[];
  readonly restoredFrom: number | null;
  readonly review: 'requested' | 'approved' | 'changes_requested' | null;
}
export interface ContactVersionsPage {
  readonly items: readonly ContactVersion[];
  readonly current: number | null;
  readonly total: number;
}
/** One saved version with the contact's details as they were saved then. */
export interface SavedContactVersion extends ContactVersion {
  readonly title: string | null;
  readonly jobTitle: string | null;
  readonly company: string | null;
  readonly address: string | null;
  readonly country: string | null;
  readonly website: string | null;
  readonly birthDate: string | null;
  readonly description: string | null;
  readonly emails: readonly EmailDto[] | null;
  readonly phones: readonly PhoneDto[] | null;
}
/** `contact` as PUT returns it; `restored: false` changed nothing. */
export interface ContactRestoreAnswer {
  readonly contact: Partial<Contact>;
  readonly version: number;
  readonly restored: boolean;
}

/**
 * contacts-app's version routes (#2071), always under the contact's own workspace. Provided per component, never at root: only
 * the space-context subtree's HttpClient carries the spaces interceptor that turns the owning-tenant context into the
 * request's space_id; the root client would send none (#2074).
 */
@Injectable()
export class ContactVersionsClient {
  private readonly http = inject(HttpClient);

  public list(contact: { id: string; accountId: string }, offset: number): Observable<ContactVersionsPage> {
    return this.http.get<ContactVersionsPage>(this.url(contact), {
      params: new HttpParams().set('offset', offset).set('limit', 25),
      context: owningTenantContext(contact.accountId),
    });
  }
  public read(contact: { id: string; accountId: string }, version: number): Observable<SavedContactVersion> {
    return this.http.get<SavedContactVersion>(`${this.url(contact)}/${version}`, {
      context: owningTenantContext(contact.accountId),
    });
  }
  public restore(contact: { id: string; accountId: string }, version: number): Observable<ContactRestoreAnswer> {
    return this.http.post<ContactRestoreAnswer>(`${this.url(contact)}/${version}/restore`, null, {
      context: owningTenantContext(contact.accountId),
    });
  }
  private url(contact: { id: string }): string {
    return `${environment.baseUrl}/api/contacts/contacts/${encodeURIComponent(contact.id)}/versions`;
  }
}
